'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@dc-shared-web/components/shadcn/form'
import { Input } from '@dc-shared-web/components/shadcn/input'
import { Button } from '@dc-shared-web/components/shadcn/button'
import { BaseDialog, Folder, useRenameFolder } from '@/dc-shared-web/src'
import { FoldersListItem } from '@/dc-shared-web/src/hooks/api/folder/types'
import { useGetFolderById } from '@/dc-shared-web/src/hooks/api/folder/useGetFolderByUuid'

const getFormSchema = (currentFolderName: string) =>
  z.object({
    name: z.coerce
      .string()
      .min(1, 'Folder name cannot be blank')
      .refine((value) => value !== currentFolderName, {
        message: `New name must be different from current name: "${currentFolderName}"`,
      }),
  })

export function RenameFolderDialog({
  folder,
  onDialogClose,
  onSuccess,
  teamUuid,
}: {
  folder: Folder | FoldersListItem
  onDialogClose: () => void
  onSuccess: () => void
  teamUuid?: string
}) {
  const { mutate: renameFolder, isLoading: isRenamingFolder } =
    useRenameFolder()

  const { data: folderData } = useGetFolderById(teamUuid ?? '', folder.uuid)

  const formSchema = getFormSchema(String(folder.name))
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: folder.name,
    },
  })

  const onRenameSubmit = async (data: z.infer<typeof formSchema>) => {
    await renameFolder({
      folderUuid: folder.uuid,
      newFolderName: data.name,
      oldFolderName: folder.name,
      office_uuid: teamUuid,
      parent_type: folderData?.parent_type,
      parent_uuid: folderData?.parent_uuid,
    })
    onSuccess()
  }

  return (
    <BaseDialog
      title='Rename folder'
      isOpen
      onClose={onDialogClose}
      primaryActionSlot={
        <Button
          disabled={isRenamingFolder}
          type='submit'
          data-testid='rename-folder-dashboard-trigger'
          onClick={form.handleSubmit(onRenameSubmit)}
        >
          {isRenamingFolder ? 'Renaming...' : 'Rename'}
        </Button>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onRenameSubmit)}>
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='tw-sr-only'>Folder name</FormLabel>
                <FormControl>
                  <Input placeholder='Enter folder name' {...field} autoFocus />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </BaseDialog>
  )
}
